// =============================================================================
// MAIN LANDING PAGE CONTENT
// =============================================================================
//
// This file contains all the copy for the main landing page (/).
// Sections are in the same order they appear on the website (top to bottom).
//
// To review: Open https://usecove.com and scroll through the page while
// cross-referencing this file. Each section is clearly labeled.
//
// APY PLACEHOLDERS: Use {apyRate} where you want the current APY rate to appear.
// Example: "Earn up to {apyRate}% APY" will become "Earn up to 4.5% APY"
//
// =============================================================================

export const landingContent = {

  // ===========================================================================
  // SECTION 1: HERO - "Build wealth. Build your island."
  // ===========================================================================
  // The top section with the main headline, app screenshot, trust badges,
  // social proof, and download buttons.
  // ---------------------------------------------------------------------------

  hero: {
    // Eyebrow text above the headline
    eyebrow: "High Yield Saving & Investing",

    // Main headline - split into two parts for styling
    headline: {
      line1: "Build wealth.",
      line2: "Build your island."
    },

    // Subheadline below the main headline
    subheadline: "Finance apps are boring. Cove turns saving and investing into an addictive game that helps you grow wealth as you play.",

    // Floating badges around the phone mockup
    floatingBadges: {
      saved: "+$50 invested",
      streak: "$12 harvested",
      rareItem: "Rare item!"
    },

    // Social proof text below the phone
    socialProof: {
      count: "30,000+",
      text: "Covelings building their islands"
    },

    // Legal disclaimer at the bottom of hero
    disclaimer: "Cove is not a bank. Please see our footer for *, ** disclaimers and more information."
  },

  // ===========================================================================
  // SECTION 2: COMPARISON - "Old Way vs Cove"
  // ===========================================================================
  // Side-by-side comparison showing "The Old Way" (boring bank) vs "The Cove Way"
  // with phone mockups and feature lists.
  // ---------------------------------------------------------------------------

  comparison: {
    heading: "Old way Vs. Cove",
    subheading: "Start an island and join the next generation of finance",

    // Labels for the two phone mockups
    oldWayLabel: "The Old Way",
    coveWayLabel: "The Cove Way",

    // Character labels below phones
    oldWayCharacter: "Normie",
    coveCharacter: "Coveling",

    // Pain points of traditional banking (shown with X icons)
    oldWayList: [
      "Saving feels like a chore",
      "Investing is too complex to start",
      "Financial apps leave you feeling stressed and unmotivated"
    ],

    // Benefits of Cove (shown with decoration images)
    coveWayList: [
      "You're addicted to saving",
      "Investing just clicked",
      "You hit your goals without trying"
    ],

    // Fake banking app content shown in the "Old Way" phone mockup
    boringBankApp: {
      logo: "BANK CO.",
      accountsTitle: "Your Accounts",
      accounts: [
        { name: "Checking", balance: "-$24.50", type: "negative" },
        { name: "Savings", balance: "$12.33", type: "positive" },
        { name: "Investment", balance: "$0.00", type: "zero" }
      ],
      overdraftNotice: {
        title: "Overdraft Fee",
        amount: "-$35.00"
      }
    }
  },

  // ===========================================================================
  // SECTION 3: TESTIMONIALS - "Wall of Love"
  // ===========================================================================
  // Carousel of user testimonials with photos and quotes.
  // Two rows scroll in opposite directions.
  // ---------------------------------------------------------------------------

  testimonials: {
    heading: "Wall of love",
    subheading: "We're proud to have a community of Covelings who love their islands",

    // Legal disclaimer for testimonials
    disclaimer: {
      text: `Names have been changed to protect client privacy. Testimonials are based on unique experiences from current clients and are not representative of all client experiences. Testimonials are unsolicited, and no cash or non-cash compensation is provided. Investing involves risk, including the possible loss of principal and fluctuation of value. Past performance is no guarantee of future results. Additional information about Eden Financial Technologies Inc. DBA Cove is available in its current disclosure documents, Form ADV, Form ADV Part 2A Brochure, and Client Relationship Summary report which are accessible online via the SEC's Investment Adviser Public Disclosure (IAPD) database at`,
      linkText: "IAPD",
      linkUrl: "https://www.adviserinfo.sec.gov",
      secNumber: "801-130646"
    },

    // First carousel row (scrolls left to right)
    row1: [
      {
        name: "Allison",
        age: "22",
        quote: "By far best savings account ever! Customer service is always helpful when issues arise, which is something I haven't got at other finance apps. If you have trouble saving like I do, I totally recommend it. Seeing my savings grow in a way that isn't just numbers in black and white on paper is amazing. I get excited everyday to open my treasure boxes and decorate my island."
      },
      {
        name: "Tay",
        age: "24",
        quote: "So far I can see a lot of future potential in building a good value folder of investments in this game. The founders do everything to fix any and all issues! I highly recommend this game to anyone looking for a fun way to invest for future returns!"
      },
      {
        name: "Avery",
        age: "25",
        quote: "This app is in the early stages but has serious potential. I love the idea of gamifying a savings account. It's done in such a cool way with personalized islands decorated by your money. I can't wait to watch this app grow with time!"
      },
      {
        name: "Jacob",
        age: "34",
        quote: "An exceptional way to save money! The app works well, it's simple and easy to use. Definitely recommend this for young people having trouble saving money."
      },
      {
        name: "Jerry",
        age: "20",
        quote: "The creator and his team came up with a great idea. You can Invest your Money And grow your savings. They came up with Great content. And one great thing about this game. The Admin will like to work with you to see if his investors have good ideas for the game that is rare.This is one of the greatest game ever I love it when you can grow your Island And invest in something you love to do. And yes, I do recommend this game. And thank you. Cove Team Keep up the great work."
      }
    ],

    // Second carousel row (scrolls right to left)
    row2: [
      {
        name: "Ben",
        age: "20",
        quote: "I absolutely love this app. If you have kids or are a beginner at investing and saving this app is a godsend. It makes saving money fun as opposed to regular bank account."
      },
      {
        name: "Sylvie",
        age: "18",
        quote: "I wasn't sure if I would use this but I really find it fun. I look forwards to the daily shipmemts and trying to find the more rare decorations to make my island special. Plus there's the bonus of saving money which make me feel great!"
      },
      {
        name: "Tom",
        age: "20",
        quote: "I am addicted. I check my crate every day to see what's new and find myself transferring money in to buy something all the time. I also can't bear the thought of loosing some of my legendary assets. I doubt I'll ever sell them"
      },
      {
        name: "Mammy",
        age: "20",
        quote: "Imagine being addicted to saving money and managing your personal finance in a way that's innovative, smart and fun. Whereas most games of this caliber cost you money, this one saves your money for you while still providing the same level of fun."
      },
      {
        name: "Tina",
        age: "29",
        quote: "A unique and fun way of banking that mixes with a social media kinda experience 😜"
      }
    ]
  },

  // ===========================================================================
  // SECTION 4: GETTING STARTED - "Get Started in Minutes"
  // ===========================================================================
  // Step-by-step onboarding flow with phone mockups showing each step.
  // Desktop: scroll-triggered progression. Mobile: auto-advancing carousel.
  // ---------------------------------------------------------------------------

  gettingStarted: {
    headline: "Get Started in Minutes",
    subheadline: "Open your account securely. Your island awaits.",

    // Four onboarding steps
    steps: [
      {
        title: "Create Your Account",
        description: "Sign up in under 2 minutes with secure identity verification. Your information is protected with bank-level encryption.",
        badge: "256-bit encryption"
      },
      {
        title: "Link Your Bank",
        description: "Connect securely through Plaid — the same technology trusted by Venmo, Robinhood, and thousands of apps. We never see your login credentials.",
        badge: "Powered by Plaid"
      },
      {
        title: "Make Your First Deposit",
        // NOTE: {apyRate} will be replaced with the current APY rate
        description: "Add funds instantly and start earning up to {apyRate}% APY right away. Your money is FDIC insured up to $1 million.",
        badge: "$1M FDIC insured**"
      },
      {
        title: "Stash Your First Decoration",
        description: "Browse the shop, stash a decoration you love, and watch it appear on your island. Your money becomes your world.",
        badge: "Your island awaits!"
      }
    ],

    // CTA at bottom of section
    cta: {
      text: "Start Your Island — It's Free",
      subtext: "No minimum deposit required"
    },

    // Scroll indicator (desktop only)
    scrollIndicator: "Scroll to continue"
  },

  // ===========================================================================
  // SECTION 5: GAME FEATURES - "Why Cove Hits Different"
  // ===========================================================================
  // Grid of game mechanics that make Cove engaging.
  // Each card shows the hook (what makes it fun) and benefit on hover.
  // ---------------------------------------------------------------------------

  gameFeatures: {
    headline: "Why Cove Hits Different",
    subheadline: "Most finance apps feel like spreadsheets. Cove feels like a game you want to play every day.",

    // Six feature cards
    features: [
      {
        title: "Daily Mystery Crates",
        hook: "Every crate reveals different decorations, but it's only available to stash today. Don't wait to stash, because tomorrow brings a new selection.",
        benefit: "The same 'limited time' excitement that hooks you in games, but now it's building your savings",
        icon: "crate"
      },
      {
        title: "Login Streaks",
        hook: "Daily login streaks with escalating rewards that grow the longer you keep it going. Miss a day? Back to zero.",
        benefit: "That satisfying streak counter becomes your daily reminder to check in on your money",
        icon: "streak"
      },
      {
        title: "Quest Board",
        hook: "Complete daily, weekly, and monthly quests for rewards. There's always something to work toward.",
        benefit: "Turn 'save $10' into a completionist's dopamine hit instead of a chore",
        icon: "quest"
      },
      {
        title: "100+ Collectible Decorations",
        hook: "Pokémon-style \"gotta catch 'em all\" collection with rarity tiers from Common to Mythic. Plus 50+ badges to earn.",
        benefit: "Complete the Decodex and you'll have over $2,000 saved without even thinking about it",
        icon: "collection"
      },
      {
        title: "Friends & Leaderboards",
        hook: "Visit friends' islands, compare collections, and compete across 8 leaderboards for Most Saved, Best Island, and more.",
        benefit: "There's no need to spend to flex, save to show off your high value island instead",
        icon: "leaderboard"
      },
      {
        title: "Island Design",
        hook: "Build a truly unique island. Customize terrain, place beaches, shape the land, and arrange every decoration exactly where you want it.",
        benefit: "The more you invest in your island, the more you invest in your future",
        icon: "island"
      }
    ],

    // Mobile tap hint
    tapHint: "Tap for more"
  },

  // ===========================================================================
  // SECTION 6: TEAM - "Meet the Team"
  // ===========================================================================
  // Team member profiles with photos, bios, and social links.
  // ---------------------------------------------------------------------------

  team: {
    heading: "Meet the team",
    subheading: "Cove was started by a group of college friends frustrated with how boring finance was. We would spend money on games and wondered - what if you could buy cool in-game items but keep the money? Nothing existed like that, so we built it.",

    // Team member profiles
    // NOTE: Images are imported separately in the component
    profiles: [
      {
        name: "David Wolfenden",
        username: "@wolfdavo",
        title: "Cofounder and CEO",
        description: "David is an obsessive entrepreneur. He leads the Cove team with focus and passion, dedicated to building the future of finance no matter the obstacles. If he's not coding, designing, writing emails, or brainstorming the next big step for Cove, you'll find him chasing the best surf."
      },
      {
        name: "Liam Howell",
        username: "@liam",
        title: "Cofounder and CTO",
        description: "Liam leads Cove's tech vision, architecting the innovative features that make Cove intuitive and engaging. He works closely with the team, tackling complex challenges to deliver only the best to the Covelings. Beyond Cove, he's surfing, foraging, or camping in the great outdoors."
      },
      {
        name: "Sarah Mirsky-Terranova",
        username: "@sarah",
        title: "CCO",
        description: "Sarah brings her expertise as a compliance executive and lawyer to help navigate regulatory complexities with ease. She empowers the team to focus on delivering value to Covelings. In her personal time, Sarah enjoys traveling and spending quality moments with her daughters and husband."
      },
      {
        name: "Ava Lazarov",
        username: "@ava",
        title: "Product & Operations",
        description: "Ava is Cove's product manager who turns data into strategic action. She has her hand in many projects across Cove so the team can stay focused on building with Covelings in mind. In her downtime, Ava loves to travel, curl up with a good book, or pursue her creative projects."
      },
      {
        name: "Jake Tisdale",
        username: "@jake",
        title: "Marketing",
        description: "Jake combines creativity and strategy to lead Cove's Marketing and Communications. With a knack for crafting campaigns and making finance approachable, he turns marketing into stories people love. When he's not in marketing mode, Jake is hiking, snapping photos, or playing his favorite games."
      },
      {
        name: "Gabe Gintalas",
        username: "@gabeg",
        title: "Game Engineer",
        description: "Gabe's sole mission is bringing imagination to Cove. Inspired by his love of video games, he designs and builds the gaming systems that bring Cove to life. Aside from his role at Cove, Gabe is an outdoorsman, a passionate soccer fan, and an avid gamer."
      }
    ]
  },

  // ===========================================================================
  // SECTION 7: FAQ - "Frequently Asked Questions"
  // ===========================================================================
  // Accordion-style FAQ with common questions and answers.
  // ---------------------------------------------------------------------------

  faq: {
    heading: "Frequently Asked Questions",
    subheading: "FAQs",

    // Questions and answers
    // NOTE: {apyRate} placeholders will be replaced with current APY
    list: [
      {
        question: "Is my money safe?",
        answer: "Yes! Your stashes and cash in your account are FDIC insured up to $1 million**, and your investments are SIPC insured up to $500,000**. We utilize best-in-class security practices, and you can withdraw at any time. Our whole team is super active in Discord too, so if you have any questions or issues come up, feel free to come chat with us!"
      },
      {
        question: "Can I earn yield?",
        answer: "Yes! You can earn up to {apyRate}%* APY on your stashes and cash in your account."
      },
      {
        question: "Can I invest in the stock market?",
        answer: "Yes! You can invest in a selection of ETFs that are selected by our team. We are constantly researching and testing new ETFs to ensure they are a good fit for our users."
      },
      {
        question: "Is Cove free?",
        answer: "It is free to open an account and save on Cove! Investments are charged a 0.8% advisory fee per year, with no additional or hidden fees. Our fee is billed monthly and is based on the average value of your investments throughout the month. This fee supports Cove's infrastructure costs and helps us take on a big and difficult industry; we appreciate your support immensely!"
      },
      {
        question: "Why do you need my social security number?",
        answer: "In accordance with federal anti money laundering and identity verification laws, we use your social security number to verify your identity and protect against fraud. We never save your social security number in our system."
      },
      {
        question: "Can I use Cove if I'm not from the US?",
        answer: "Unfortunately, Cove is only available to those with a social security number and a US bank account - Cash App and PayPal accounts are not supported. However, we are planning to expand access to those outside of the US in the near future."
      }
    ]
  },

  // ===========================================================================
  // SECTION 8: CALL TO ACTION - "Stop managing money. Start mastering the game."
  // ===========================================================================
  // Final CTA section with headline, benefits checklist, and download buttons.
  // ---------------------------------------------------------------------------

  callToAction: {
    heading: "Stop managing money. Start mastering the game.",
    subheading: "See your progress, build your wealth, and have fun doing it.",

    // Benefit checklist (shown with checkmarks)
    // NOTE: {apyRate} placeholder will be replaced with current APY
    benefits: [
      "Start building your island for free",
      "Grow your savings effortlessly with up to {apyRate}% APY*",
      "Make saving and investing as fun as spending"
    ]
  },

  // ===========================================================================
  // SECTION 9: FOOTER
  // ===========================================================================
  // Site footer with navigation, legal links, social media, and disclaimers.
  // ---------------------------------------------------------------------------

  footer: {
    // Main navigation links
    navigation: [
      { name: "Families", href: "/families" },
      { name: "How it works", href: "#howitworkssection" },
      { name: "Benefits", href: "#benefitssection" },
      { name: "About us", href: "#aboutus" },
      { name: "Blog", href: "/blog" },
      { name: "FAQs", href: "#faqsection" },
      { name: "Legal", href: "/legal" }
    ],

    // Legal document links
    legalLinks: [
      { name: "Privacy Policy", url: "/privacy-policy" },
      { name: "Terms of Service", url: "/legal/terms-of-service" },
      { name: "Investment Advisory Agreement", url: "/legal/iaa" },
      { name: "Alpaca Customer Agreement", url: "https://files.alpaca.markets/disclosures/library/AcctAppMarginAndCustAgmt.pdf" },
      { name: "Cash Sweep Program", url: "https://files.alpaca.markets/disclosures/Alpaca+FDIC+Bank+Sweep+Program+Terms+and+Conditions.pdf" },
      { name: "Cove Firm Brochure (ADV Part 2A)", url: "https://files.adviserinfo.sec.gov/IAPD/Content/Common/crd_iapd_Brochure.aspx?BRCHR_VRSN_ID=979558" },
      { name: "Cove Client Relationship Summary", url: "https://reports.adviserinfo.sec.gov/crs/crs_329900.pdf" },
      { name: "Referral Program Policy", url: "/legal/referral-program-policy" }
    ],

    // Social media links
    socialMedia: {
      x: "https://x.com/use_cove",
      instagram: "https://www.instagram.com/use.cove/",
      linkedin: "https://www.linkedin.com/company/usecove",
      facebook: "https://www.facebook.com/profile.php?id=100070767042300",
      discord: "https://discord.gg/NhxjaKRA9T"
    }
  },

  // ===========================================================================
  // NAVIGATION BAR
  // ===========================================================================
  // Top navigation bar links and buttons.
  // ---------------------------------------------------------------------------

  navigation: {
    links: [
      { name: "Families", href: "/families" },
      { name: "How it works", href: "#howitworkssection" },
      { name: "Benefits", href: "#benefitssection" },
      { name: "About us", href: "#aboutus" },
      { name: "Blog", href: "/blog" },
      { name: "FAQs", href: "#faqsection" }
    ],

    // Families badge text
    familiesBadge: "beta",

    // Download button text (compact navbar button)
    downloadButton: "Download"
  },

  // ===========================================================================
  // APP STORE LINKS
  // ===========================================================================
  // Download links for App Store and Google Play.
  // Used by download buttons throughout the site.
  // ---------------------------------------------------------------------------

  appStoreLinks: {
    appStore: "https://apps.apple.com/us/app/cove-your-money-haven/id1589645033",
    playStore: "https://play.google.com/store/apps/details?id=com.EdenFinancialTechnologies.Eden"
  },

  // ===========================================================================
  // METADATA
  // ===========================================================================
  // SEO and social sharing metadata.
  // ---------------------------------------------------------------------------

  metadata: {
    title: "Cove",
    description: "The money app that isn't boring. Save, invest, and grow your money with Cove.",
    url: "https://usecove.com/",
    icon: "https://usecove.com/favicon.ico",
    thumbnail: "https://usecove.com/opengraphimage.webp"
  }
};
